import {LetrusApi} from '@letrustech/letrus-api-interfaces';
import {
  applyAndClearHighlights,
  highlightMistakes,
} from 'components/BasicEditor/plugins/LetrusHighlighter';
import {EditorState} from 'draft-js';
import createCompositeDecorator from 'draft-js-plugins-editor/lib/Editor/createCompositeDecorator';
import MultiDecorator from 'draft-js-plugins-editor/lib/Editor/MultiDecorator';
import {fromJS, Map} from 'immutable';
import isMobile from 'ismobilejs';
import memoize from 'lodash.memoize';
import {AnyAction, Reducer} from 'redux';
import {call, put} from 'redux-saga/effects';
import {createSelector} from 'reselect';
import {action} from 'typesafe-actions';
import {reviveEditorState} from 'utils/functions/reviveEditorState';
import {
  fetchCompositionByIdService,
  fetchCompositionJsonStatsAsyncService,
  writeCompositionService,
  WriteCompositionServiceData,
} from '../../services/compositionServices';

// weeird stuff from the editor, if one day in the future, letrus get it's own editor and stop using draftjs, remove it!
const getDecorator = memoize(function getDecorator(highlightName) {
  return new MultiDecorator([
    createCompositeDecorator([
      highlightMistakes({
        highlightName,
      }),
    ]),
  ]);
});

// Action types
export enum CompositionsTypes {
  FETCH_BY_ID_REQUEST = '@compositions/FETCH_BY_ID_REQUEST',
  FETCH_BY_ID_SUCCESS = '@compositions/FETCH_BY_ID_SUCCESS',
  FETCH_BY_ID_FAILURE = '@compositions/FETCH_BY_ID_FAILURE',

  WRITE_COMPOSITION_REQUEST = '@compositions/WRITE_COMPOSITION_REQUEST',
  WRITE_COMPOSITION_SUCCESS = '@compositions/WRITE_COMPOSITION_SUCCESS',
  WRITE_COMPOSITION_FAILURE = '@compositions/WRITE_COMPOSITION_FAILURE',

  FETCH_COMPOSITION_JSON_STATS_ASYNC_REQUEST = '@compositions/FETCH_COMPOSITION_JSON_STATS_ASYNC_REQUEST',
  FETCH_COMPOSITION_JSON_STATS_ASYNC_SUCCESS = '@compositions/FETCH_COMPOSITION_JSON_STATS_ASYNC_SUCCESS',
  FETCH_COMPOSITION_JSON_STATS_ASYNC_FAILURE = '@compositions/FETCH_COMPOSITION_JSON_STATS_ASYNC_FAILURE',

  SET_HIGHLIGHT = '@compositions/SET_HIGHLIGHT',

  SET_COMPOSITION_EDITOR_STATE = '@compositions/SET_COMPOSITION_EDITOR_STATE',

  SET_COMPOSITION_LOCAL_STATE = '@compositions/SET_COMPOSITION_LOCAL_STATE',

  CLEAR_PARENT_OR_CHILD = '@compositions/CLEAR_PARENT_OR_CHILD',

  RESET_SAVE_COMPOSITION_FAILURE_MESSAGE = '@compositions/RESET_SAVE_COMPOSITION_FAILURE_MESSAGE',
}

// Data types

// State type
export interface CompositionsState extends Map<string, any> {
  readonly data: ImmutableMap<any> | undefined;
  readonly loading: boolean;
  readonly error: number | undefined;
  readonly dataCount: number;

  readonly isLoadingCompositionsJsonStatsAsync: boolean;
  readonly compositionsJsonStatsAsyncError: boolean;
  readonly compositionsJsonStatsAsync: ImmutableMap<any>;

  readonly isLoadingCompositionById: boolean;
  readonly compositionById: ImmutableMap<LetrusApi.CompositionDetail>;

  readonly errorAt: null | Date;
  readonly loadingSaveComposition: boolean;
  readonly hasSavedComposition: boolean;

  readonly editorState: any;
  readonly highlight: {highlight: any; highlightName: string};
}

// Fetch actions
export const fetchCompositionByIdRequest = (compositionId?: number | string) =>
  action(CompositionsTypes.FETCH_BY_ID_REQUEST, {compositionId});

export const fetchCompositionByIdSuccess = (
  data: LetrusApi.CompositionDetail,
) => action(CompositionsTypes.FETCH_BY_ID_SUCCESS, {data});

export const fetchCompositionByIdFailure = () =>
  action(CompositionsTypes.FETCH_BY_ID_FAILURE);

export const writeCompositionRequest = (
  composition: WriteCompositionServiceData,
) =>
  action(CompositionsTypes.WRITE_COMPOSITION_REQUEST, {
    composition,
  });

export const writeCompositionSuccess = (data: any) =>
  action(CompositionsTypes.WRITE_COMPOSITION_SUCCESS, {data});

export const writeCompositionFailure = () =>
  action(CompositionsTypes.WRITE_COMPOSITION_FAILURE);

export const fetchCompositionJsonStatsAsyncRequest = (
  composition?: any,
  params?: any,
) =>
  action(CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_REQUEST, {
    composition,
    params,
  });

export const fetchCompositionJsonStatsAsyncSuccess = (data: any) =>
  action(CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_SUCCESS, {data});

export const fetchCompositionJsonStatsAsyncFailure = () =>
  action(CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_FAILURE);

export const setHighlight = ({highlight, highlightName}) =>
  action(CompositionsTypes.SET_HIGHLIGHT, {
    highlight,
    highlightName,
  });

export const clearParentOrChildComposition = () =>
  action(CompositionsTypes.CLEAR_PARENT_OR_CHILD);

export const setCompositionEditorState = (editorStateUpdate) =>
  action(CompositionsTypes.SET_COMPOSITION_EDITOR_STATE, {
    editorStateUpdate,
  });

export const setCompositionLocalState = (composition: any) =>
  action(CompositionsTypes.SET_COMPOSITION_LOCAL_STATE, {
    composition,
  });

// Sagas
export function* fetchCompositionById(action: AnyAction) {
  try {
    const response = yield call(
      fetchCompositionByIdService,
      action.payload.compositionId,
    );
    yield put(fetchCompositionByIdSuccess(response.data));
  } catch (err) {
    yield put(fetchCompositionByIdFailure());
  }
}

export function* writeComposition(action: AnyAction) {
  try {
    const response = yield call(
      writeCompositionService,
      action.payload.composition,
    );
    yield put(writeCompositionSuccess(response.data));
  } catch (err) {
    yield put(writeCompositionFailure());
  }
}

export function* fetchCompositionJsonStatsAsync(action: AnyAction) {
  try {
    const response = yield call(
      fetchCompositionJsonStatsAsyncService,
      action.payload.composition,
      action.payload.params,
    );

    yield put(fetchCompositionJsonStatsAsyncSuccess(response));
  } catch (err) {
    yield put(fetchCompositionJsonStatsAsyncFailure());
  }
}

// Selectors
const compositionsSelector = (state) => state.get('compositions');

export const getIletrusScore = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('iletrus_tensor_flow_score'),
);

export const getCompositionJsonStatsAsync = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('compositionsJsonStatsAsync'),
);

export const getCompositionById = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('compositionById'),
);

export const getCompositionIsFundII = createSelector(
  compositionsSelector,
  (compositions) =>
    compositions.getIn(['compositionById', 'genre', 'review_grid_name']) ===
    'Grade Única',
);

export const getIsLoadingCompositionById = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('isLoadingCompositionById'),
);

export const isLoadingCompositionsJsonStatsAsync = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('isLoadingCompositionsJsonStatsAsync'),
);

export const getParentOrChildCompositionById = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('parentOrChildCompositionById'),
);

export const isLoadingParentOrChildCompositionById = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('isLoadingParentOrChildCompositionById'),
);

export const getErrorAt = createSelector(compositionsSelector, (compositions) =>
  compositions.get('errorAt'),
);

export const getIsLoadingSaveComposition = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('loadingSaveComposition'),
);

export const hasSavedComposition = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('hasSavedComposition'),
);

export const getEditorState = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('editorState'),
);

export const getCompositionFetchError = createSelector(
  compositionsSelector,
  (compositions) => compositions.get('compositionFetchError'),
);

// Initial state
export const INITIAL_STATE: CompositionsState = fromJS({
  data: fromJS([]),
  error: false,
  loading: false,
  dataCount: 0,
  isLoadingCompositionsJsonStatsAsync: false,
  compositionsJsonStatsAsyncError: false,
  compositionsJsonStatsAsync: fromJS({}),
  isLoadingCompositionById: false,
  compositionById: fromJS({}),
  errorAt: null,
  loadingSaveComposition: false,
  hasSavedComposition: false,
  editorState: null,
  highlight: null,
  compositionFetchError: false,
});

// Reducer
export const reducer: Reducer<CompositionsState> = (
  state = INITIAL_STATE,
  action,
) => {
  switch (action.type) {
    case CompositionsTypes.FETCH_BY_ID_REQUEST:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingCompositionById', true)
          .set('compositionsJsonStatsAsync', fromJS({}))
          .set('compositionsJsonStatsAsyncError', false)
          .set('error', false)
          .set('errorAt', null)
          .set('compositionFetchError', false),
      );

    // TODO: Na migração do EditorPage
    case CompositionsTypes.FETCH_BY_ID_SUCCESS:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingCompositionById', false)
          .set('error', false)
          .set('errorAt', null)
          .set('compositionById', fromJS(action.payload.data))
          .set('editorState', reviveEditorState(action.payload.data))
          .set('compositionFetchError', false),
      );

    case CompositionsTypes.FETCH_BY_ID_FAILURE:
      return state.withMutations((prevState) => {
        return prevState
          .set('compositionFetchError', true)
          .set('isLoadingCompositionById', false)
          .set('error', true);
      });

    case CompositionsTypes.WRITE_COMPOSITION_REQUEST:
      return state.withMutations((prevState) =>
        prevState
          .set('loadingSaveComposition', true)
          .set('hasSavedComposition', false)
          .set('errorAt', null),
      );

    case CompositionsTypes.WRITE_COMPOSITION_SUCCESS: {
      if (state.get('compositionById')?.size) {
        return state.withMutations((prevState) =>
          prevState
            .set('loadingSaveComposition', false)
            .set('hasSavedComposition', true)
            .set('errorAt', null)
            .set(
              'compositionById',
              fromJS({
                ...state.get('compositionById').toJS(),
                ...{
                  id: action.payload.data.id,
                  title: action.payload.data.title,
                  composition_raw: action.payload.data.composition_raw,
                  finished: action.payload.data.finished,
                },
              }),
            ),
        );
      }

      return state.withMutations((prevState) =>
        prevState
          .set('loadingSaveComposition', false)
          .set('hasSavedComposition', false)
          .set('compositionById', fromJS(action.payload.data)),
      );
    }

    case CompositionsTypes.WRITE_COMPOSITION_FAILURE:
      return state.withMutations((prevState) =>
        prevState
          .set('loadingSaveComposition', false)
          .set('errorAt', new Date()),
      );

    case CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_REQUEST:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingCompositionsJsonStatsAsync', true)
          .set('compositionsJsonStatsAsyncError', false),
      );

    case CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_SUCCESS:
      return state.withMutations((prevState) => {
        // when data has message attribute is an Error payload
        if (action.payload.data?.message) {
          return prevState
            .set('isLoadingCompositionsJsonStatsAsync', false)
            .set('compositionsJsonStatsAsyncError', true);
        }
        return prevState
          .set('isLoadingCompositionsJsonStatsAsync', false)
          .set('compositionsJsonStatsAsync', fromJS(action.payload.data))
          .set('compositionsJsonStatsAsyncError', false);
      });

    case CompositionsTypes.FETCH_COMPOSITION_JSON_STATS_ASYNC_FAILURE:
      return state.withMutations((prevState) =>
        prevState
          .set('isLoadingCompositionsJsonStatsAsync', false)
          .set('compositionsJsonStatsAsyncError', true),
      );

    case CompositionsTypes.SET_HIGHLIGHT: {
      const highlight = {
        highlight: action.payload.highlight,
        highlightName: action.payload.highlightName,
      };

      const oldHighlight = state.get('highlight');
      const editorState = state.get('editorState');

      if (!editorState?.getCurrentContent) {
        return state.withMutations((prevState) =>
          prevState.set('highlight', highlight),
        );
      }

      if ((isMobile as any).any) {
        return state.withMutations((prevState) =>
          prevState.set('highlight', highlight),
        );
      }

      return state
        .withMutations((prevState) => prevState.set('highlight', highlight))
        .set(
          'editorState',
          EditorState.set(
            applyAndClearHighlights({
              editorState,
              highlightName: highlight.highlightName,
              newHighlight: highlight.highlight,
              oldHighlight: !oldHighlight ? null : oldHighlight.highlight,
              oldHighlightName: !oldHighlight
                ? null
                : oldHighlight.highlightName,
            }),
            {
              decorator: getDecorator(highlight.highlightName),
            },
          ),
        );
    }

    case CompositionsTypes.SET_COMPOSITION_EDITOR_STATE:
      return state.withMutations((prevState) =>
        prevState.set('editorState', action.payload.editorStateUpdate),
      );

    case CompositionsTypes.SET_COMPOSITION_LOCAL_STATE:
      return state.withMutations((prevState) =>
        prevState.set('compositionById', action.payload.composition),
      );

    case CompositionsTypes.CLEAR_PARENT_OR_CHILD:
      return state.withMutations((prevState) =>
        prevState.set('parentOrChildComposition', fromJS({})),
      );

    default:
      return state;
  }
};

export default reducer;
